import React, { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Calendar, MapPin, ArrowRight, Video, X, ChevronLeft, ChevronRight } from "lucide-react"
import Back from "../common/back/Back"
import { collection, getDocs, addDoc, serverTimestamp } from "firebase/firestore"
import { db } from "../../firebase"
import "./events.css"

const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
    } catch (e) {
        return dateStr;
    }
};

const galleryImages = [
    "/images/gallery/img1.jpeg",
    "/images/gallery/img2.jpeg",
    "/images/gallery/img3.jpeg",
    "/images/gallery/img4.jpeg",
    "/images/gallery/img5.jpeg",
    "/images/gallery/img6.jpeg",
    "/images/gallery/img7.jpeg",
    "/images/gallery/img8.jpeg",
    "/images/gallery/img9.jpeg",
    "/images/gallery/img10.jpeg",
    "/images/gallery/img11.jpeg",
    "/images/gallery/img12.jpeg",
    "/images/gallery/img14.jpeg",
    "/images/gallery/img15.jpeg",
]

const swipeConfidenceThreshold = 10000;
const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
};

const Events = () => {
    const [events, setEvents] = useState([])
    const [loading, setLoading] = useState(true)
    const [lightboxIndex, setLightboxIndex] = useState(null)
    const [registrationModal, setRegistrationModal] = useState({ isOpen: false, eventId: null, eventName: "" })
    const [regForm, setRegForm] = useState({ name: "", email: "", phone: "" })
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                setLoading(true)
                const snap = await getDocs(collection(db, "events"))
                const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                // Sort by date ascending (closest events first)
                list.sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0))
                setEvents(list)
            } catch (error) {
                console.error("Error fetching events:", error)
            } finally {
                setLoading(false)
            }
        }
        fetchEvents()
    }, [])

    const handleRegisterSubmit = async (e) => {
        e.preventDefault()
        if (!regForm.name || !regForm.email || !regForm.phone) return alert("Please fill all required fields")
        
        try {
            setSubmitting(true)
            await addDoc(collection(db, "event_registrations"), {
                ...regForm,
                eventId: registrationModal.eventId,
                eventName: registrationModal.eventName,
                createdAt: serverTimestamp()
            })
            alert("Registration successful! We will contact you soon.")
            setRegistrationModal({ isOpen: false, eventId: null, eventName: "" })
            setRegForm({ name: "", email: "", phone: "" })
        } catch (error) {
            console.error("Registration error:", error)
            alert("Something went wrong. Please try again later.")
        } finally {
            setSubmitting(false)
        }
    }

    // Keyboard navigation for lightbox
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (lightboxIndex === null) return;
            if (e.key === "Escape") setLightboxIndex(null);
            if (e.key === "ArrowRight") nextImage(e);
            if (e.key === "ArrowLeft") prevImage(e);
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [lightboxIndex]);

    const openLightbox = (index) => setLightboxIndex(index);
    const closeLightbox = () => setLightboxIndex(null);
    
    const nextImage = (e) => {
        if(e) e.stopPropagation();
        setLightboxIndex((prev) => (prev + 1) % galleryImages.length);
    };
    
    const prevImage = (e) => {
        if(e) e.stopPropagation();
        setLightboxIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
    };

    if (loading) {
        return (
            <div className="events-page">
                <Back 
                    title="Upcoming Events" 
                    heroImages={[
                        "/images/gallery/img02.jpeg",
                        "/images/gallery/img01.jpeg",
                        "/images/gallery/img03.jpeg"
                    ]} 
                />
                <section className="events-section">
                    <div className="container">
                        <div className="events-grid">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="event-card animate-pulse bg-white/5 h-64 rounded-2xl" />
                            ))}
                        </div>
                    </div>
                </section>
            </div>
        )
    }

    return (
        <div className="events-page">
            <Back 
                title="Upcoming Events" 
                heroImages={[
                    "/images/gallery/img02.jpeg",
                    "/images/gallery/img01.jpeg",
                    "/images/gallery/img03.jpeg"
                ]} 
            />

            <section className="events-section">
                <div className="container">
                    <div className="events-grid">
                        {events.length === 0 ? (
                            <div className="col-span-full text-center py-16 text-gray-400">
                                <Calendar size={48} className="mx-auto mb-4 opacity-50" />
                                <p className="text-lg font-medium">No upcoming events scheduled</p>
                                <p className="text-sm opacity-70">Check back later for updates!</p>
                            </div>
                        ) : (
                            events.map((event, idx) => (
                                <motion.div
                                    key={event.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.1 }}
                                    className="event-card"
                                >
                                    <div className="event-tag">
                                        {event.isVirtual ? <Video size={14} /> : <MapPin size={14} />}
                                        {event.type}
                                    </div>

                                    <h3 className="event-title">{event.title}</h3>

                                    <div className="event-meta">
                                        <div className="meta-item">
                                            <Calendar size={16} />
                                            <span>{formatDate(event.date)}</span>
                                        </div>
                                        <div className="meta-item">
                                            <MapPin size={16} />
                                            <span>{event.location}</span>
                                        </div>
                                    </div>

                                    <p className="event-desc">{event.desc}</p>

                                    <button 
                                        className="event-link" 
                                        onClick={() => setRegistrationModal({ isOpen: true, eventId: event.id, eventName: event.title })}
                                    >
                                        Register Now <ArrowRight size={16} />
                                    </button>
                                </motion.div>
                            ))
                        )}
                    </div>
                </div>
            </section>

            <section className="gallery-section">
                <div className="container">
                    <h2 className="gallery-title text-center">
                        <span className="text-gradient">Event Gallery</span>
                    </h2>
                </div>
                <div className="gallery-slider-container">
                    <div className="gallery-track">
                        {/* Duplicate the images to create a seamless infinite scroll loop */}
                        {[...galleryImages, ...galleryImages].map((img, idx) => (
                            <div 
                                key={idx} 
                                className="gallery-slide"
                                onClick={() => openLightbox(idx % galleryImages.length)}
                            >
                                <img src={img} alt={`Students receiving expert visa counseling from Welsh Education Bathinda Punjab - Memory ${idx + 1}`} loading="lazy" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Lightbox Modal */}
            <AnimatePresence>
                {lightboxIndex !== null && (
                    <motion.div 
                        className="lightbox-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeLightbox}
                    >
                        <button className="lightbox-close" onClick={closeLightbox}>
                            <X size={36} />
                        </button>
                        
                        <button className="lightbox-nav left" onClick={prevImage}>
                            <ChevronLeft size={48} />
                        </button>
                        
                        <motion.img 
                            key={lightboxIndex}
                            src={galleryImages[lightboxIndex]} 
                            alt="Student consulting visa expert at Welsh Education Bathinda"
                            className="lightbox-img"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.2 }}
                            onClick={(e) => e.stopPropagation()}
                            drag="x"
                            dragConstraints={{ left: 0, right: 0 }}
                            dragElastic={0.2}
                            onDragEnd={(e, { offset, velocity }) => {
                                const swipe = swipePower(offset.x, velocity.x);
                                if (swipe < -swipeConfidenceThreshold) {
                                    nextImage(null);
                                } else if (swipe > swipeConfidenceThreshold) {
                                    prevImage(null);
                                }
                            }}
                        />

                        <button className="lightbox-nav right" onClick={nextImage}>
                            <ChevronRight size={48} />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Registration Modal */}
            <AnimatePresence>
                {registrationModal.isOpen && (
                    <motion.div 
                        className="registration-modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setRegistrationModal({ isOpen: false, eventId: null, eventName: "" })}
                    >
                        <motion.div 
                            className="registration-modal-content"
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="registration-modal-header">
                                <h3>Register for Event</h3>
                                <button onClick={() => setRegistrationModal({ isOpen: false, eventId: null, eventName: "" })} className="close-modal-btn">
                                    <X size={24} />
                                </button>
                            </div>
                            <p className="registration-modal-subtitle">
                                You are registering for: <span>{registrationModal.eventName}</span>
                            </p>
                            
                            <form onSubmit={handleRegisterSubmit} className="registration-form">
                                <div className="form-group">
                                    <label>Full Name *</label>
                                    <input required type="text" value={regForm.name} onChange={(e) => setRegForm({...regForm, name: e.target.value})} />
                                </div>
                                <div className="form-group">
                                    <label>Email Address *</label>
                                    <input required type="email" value={regForm.email} onChange={(e) => setRegForm({...regForm, email: e.target.value})} />
                                </div>
                                <div className="form-group">
                                    <label>Phone Number *</label>
                                    <input required type="tel" value={regForm.phone} onChange={(e) => setRegForm({...regForm, phone: e.target.value})} />
                                </div>
                                <button disabled={submitting} type="submit" className="submit-btn">
                                    {submitting ? "Submitting..." : "Confirm Registration"}
                                </button>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default Events
